/**
 * SearchPanel 面包屑构建与解析
 *
 * breadcrumbs 数组不含 SearchPanel 默认的「全部账户」项；
 * 与 URL 层级恢复、表格下钻、session 恢复共用同一套结构。
 *
 * 面包屑项结构：
 * {
 *   type: 'campaign' | 'ad',       // 该面包屑代表的层级
 *   id: string,                    // 当前层级的标识 ID
 *   name: string,                  // 显示名称（i18n key 或真实名称）
 *   num?: number,                  // 多 campaign 选中数量（仅多选时有值）
 *   tab?: string,                  // 'campaigns' 表示多 campaign 中间层
 *   origin: { accountId, campaignId? }  // 原始 ID 引用，供 handleSearch 构造请求参数
 * }
 */
import { isMultiCampaignSelection, resolveCampaignIds } from './normalizeQuery';

/**
 * 多选 campaign 进入 ad 层时的面包屑显示名 / i18n key
 * 与 campaign/index.vue handleSelectView 保持一致
 */
export const MULTI_CAMPAIGN_BREADCRUMB_NAME = 'msn.dashboard.campaignReview';

/**
 * 构建 campaign 层级的面包屑项
 * 多 campaign 时带 num + tab='campaigns' 标记
 * @param {string} campaignId   逗号分隔的 campaign ID
 * @param {string} campaignName 显示名称
 * @param {Object} origin       { accountId, campaignId }
 */
function buildCampaignCrumb(campaignId, campaignName, origin) {
    const crumb = { type: 'campaign', id: campaignId, name: campaignName || campaignId, origin };
    if (isMultiCampaignSelection(campaignId)) {
        crumb.num = resolveCampaignIds(campaignId).length;
        crumb.name = MULTI_CAMPAIGN_BREADCRUMB_NAME;
        crumb.tab = 'campaigns';
    }
    return crumb;
}

/**
 * 根据层级状态构建 SearchPanel.breadcrumbs
 *
 * 不同层级的面包屑路径：
 * - account：空数组（SearchPanel 默认显示「全部账户」）
 * - campaign（单 campaign 汇总）：[accountCrumb]
 * - campaign（多 campaign 中间层，singleCapFlag=false）：[accountCrumb, campaignCrumb]
 * - ad：[accountCrumb, campaignCrumb, adCrumb]
 *
 * @param {Object} params
 * @param {'account'|'campaign'|'ad'} params.type        目标层级
 * @param {string}  params.accountId                     账户 ID
 * @param {string}  params.accountName                   账户显示名
 * @param {string}  [params.campaignId]                  campaign ID（多选时逗号分隔）
 * @param {string}  [params.campaignName]                campaign 显示名
 * @param {boolean} [params.singleCapFlag=true]          true=单 campaign 汇总，false=多 campaign 中间层
 */
export function buildBreadcrumbsFromState({
    type, accountId, accountName, campaignId, campaignName, singleCapFlag
}) {
    if (type === 'account') return [];

    // 第一级：account → campaign 的入口面包屑
    const accountCrumb = {
        type: 'campaign',
        id: accountId,
        name: accountName,
        origin: { accountId }
    };

    if (type === 'campaign') {
        // 多 campaign 中间层：需要额外展示 campaign 面包屑
        // tab='campaigns' 必须无条件设置，resolveBreadcrumbSingleCapFlag 依赖它判断层级类型
        if (!singleCapFlag && campaignId) {
            const crumb = buildCampaignCrumb(campaignId, campaignName, { accountId, campaignId });
            crumb.tab = 'campaigns';
            return [accountCrumb, crumb];
        }
        // 单 campaign 汇总层：只显示 account 面包屑
        return [accountCrumb];
    }

    if (type === 'ad' && campaignId) {
        const origin = { accountId, campaignId };
        const isMulti = isMultiCampaignSelection(campaignId);
        const displayName = isMulti ? MULTI_CAMPAIGN_BREADCRUMB_NAME : (campaignName || campaignId);
        const campaignCrumb = buildCampaignCrumb(campaignId, campaignName, origin);
        // ad 层面包屑沿用 campaign 的 name/num，仅改 type
        const adCrumb = { type: 'ad', id: campaignId, name: displayName, origin };
        if (isMulti) adCrumb.num = resolveCampaignIds(campaignId).length;
        return [accountCrumb, campaignCrumb, adCrumb];
    }

    return [];
}

/**
 * 判断面包屑点击目标是否为「单 campaign 汇总」层
 *
 * 判断依据（任一满足则为多 campaign 中间层 → 返回 false）：
 * 1. 面包屑有 tab='campaigns' 标记 → 点击的是多 campaign 中间层
 * 2. 当前是 campaign 类型、带 campaignId、且下一级是 ad → 处于多 campaign → ad 的路径中
 *
 * @param {Object} crumb          当前点击的面包屑项
 * @param {Array}  breadcrumbs    完整面包屑（含默认「全部账户」）
 * @param {number} activeIndex    当前项在 breadcrumbs 中的下标
 * @returns {boolean} true = 单 campaign 汇总层，false = 多 campaign 中间层
 */
export function resolveBreadcrumbSingleCapFlag(crumb, breadcrumbs, activeIndex) {
    if (crumb.tab === 'campaigns') return false;
    const next = breadcrumbs[activeIndex + 1];
    if (crumb.type === 'campaign' && crumb.origin?.campaignId && next?.type === 'ad') return false;
    return true;
}
